// src/features/analytics/useAnalyticsRange.ts

import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { analyticsRouteApi } from './routeApi';
import { useAnalyticsMeta } from './useAnalyticsMeta';
import {
  browserDayZone,
  resolveAnalyticsRange,
  resolveSelectedAnalyticsRange,
  type AnalyticsSearch,
} from './searchParams';
import type { DayZone, UtcRange } from '@/shared/utils/utcRange';

export interface UseAnalyticsRangeOptions {
  /**
   * Which calendar days the caller's data source speaks: `'auto'` (default)
   * = the browser's local days once the gateway declares `features.tz`, else
   * UTC; `'utc'` = always UTC days — the Audience, Delivery, and Sub-Accounts
   * tabs and every per-media UTC-day route (dashboard ADR-009).
   */
  days?: 'auto' | 'utc';
}

export interface UseAnalyticsRangeResult {
  search: AnalyticsSearch;
  /** Floored fetch range — what queries send (the gateway rejects pre-window). */
  range: UtcRange;
  /**
   * What the customer actually selected (no product-window floor). Differs
   * from `range` only when a preset reaches back before the window; the
   * coverage notice names THIS span so a clamped "Last 90 days" still says so.
   */
  selectedRange: UtcRange;
  /** UTC midnight of T when configured (Phase 6); null through Phase 5. */
  productWindowFloorSeconds: number | null;
  /** The calendar-day zone the ranges are aligned to: 'utc' or the browser's IANA zone. */
  zone: DayZone;
  /** The `tz` request value to send (undefined for UTC days). */
  tz: string | undefined;
  /** True when the ranges are local calendar days (labels read "(local)"). */
  localDays: boolean;
}

/**
 * Read the validated /analytics search params and resolve them to concrete
 * end-exclusive epoch-second ranges (floored fetch range + unfloored selected
 * range), aligned to UTC days or — on the local-day surfaces, once the
 * gateway declares `features.tz` — to the browser's local calendar days
 * (dashboard ADR-009). The URL stores instants either way, so a link opened
 * in another zone snaps to THAT viewer's days.
 */
export function useAnalyticsRange(options: UseAnalyticsRangeOptions = {}): UseAnalyticsRangeResult {
  const search = analyticsRouteApi.useSearch();
  const { productWindowFloorSeconds, features } = useAnalyticsMeta();
  const days = options.days ?? 'auto';

  const zone: DayZone = useMemo(() => {
    if (days === 'utc' || !features.tz) return 'utc';
    return browserDayZone(DateTime.local().zoneName);
  }, [days, features.tz]);

  const { range, selectedRange } = useMemo(() => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    return {
      range: resolveAnalyticsRange(search, nowSeconds, productWindowFloorSeconds, zone),
      selectedRange: resolveSelectedAnalyticsRange(search, nowSeconds, zone),
    };
  }, [search, productWindowFloorSeconds, zone]);

  return {
    search,
    range,
    selectedRange,
    productWindowFloorSeconds,
    zone,
    tz: zone === 'utc' ? undefined : zone,
    localDays: zone !== 'utc',
  };
}
